import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Signature, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/**
 * SignatureGalleryPicker — displays admin's saved signature library.
 * Clicking a signature sets it as the current SPB signature.
 *
 * Props:
 *  - value: current signature data URL (string) or null
 *  - onChange(dataUrl | null)
 */
export default function SignatureGalleryPicker({ value, onChange, autoSelectFirst = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    api.get("/signatures", { params: { aktif: true } })
      .then(({ data }) => {
        setItems(data);
        // Auto-attach the first active signature so admins don't need to click.
        if (autoSelectFirst && data.length > 0 && !value) {
          const first = data[0];
          setSelectedId(first.id);
          onChange && onChange(`data:image/png;base64,${first.image_data}`);
        }
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPick = (sig) => {
    if (selectedId === sig.id) {
      setSelectedId(null);
      onChange && onChange(null);
      return;
    }
    setSelectedId(sig.id);
    onChange && onChange(`data:image/png;base64,${sig.image_data}`);
  };

  const clearSelection = () => {
    setSelectedId(null);
    onChange && onChange(null);
  };

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Memuat library...</div>;

  if (items.length === 0) {
    return (
      <div className="border border-dashed border-border p-6 text-center" data-testid="sig-empty">
        <Signature size={40} className="mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground mb-3">
          Belum ada tanda tangan di library.
        </p>
        <Link to="/settings/signatures">
          <Button variant="outline" className="rounded-none" data-testid="sig-manage-link">
            Kelola Library
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="signature-gallery">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Klik gambar untuk memilih. Klik lagi untuk membatalkan.
        </p>
        <div className="flex gap-2">
          {value && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-none"
              onClick={clearSelection}
              data-testid="sig-clear-selection"
            >
              <Trash size={14} className="mr-1" /> Batalkan Pilihan
            </Button>
          )}
          <Link to="/settings/signatures">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-none"
              data-testid="sig-manage-link"
            >
              Kelola Library
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((sig) => {
          const isSelected = selectedId === sig.id;
          return (
            <button
              type="button"
              key={sig.id}
              onClick={() => onPick(sig)}
              data-testid={`sig-pick-${sig.id}`}
              className={`border-2 p-2 bg-white text-left transition-all ${
                isSelected
                  ? "border-primary ring-2 ring-primary/30"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <div className="h-20 flex items-center justify-center border border-border/40 bg-white mb-2 overflow-hidden">
                <img
                  src={`data:image/png;base64,${sig.image_data}`}
                  alt={sig.name}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div className="text-xs font-bold truncate">{sig.name}</div>
              {isSelected && (
                <div className="text-[10px] text-primary font-bold uppercase tracking-wider mt-1">
                  ✓ Terpilih
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
